import themes from '~/common/passporter-styles/themes'

export enum BUTTON_TYPE {
	FILLED = 'filled',
	OUTLINED = 'outlined',
	TEXT = 'text',
	GHOST = 'ghost',
}

export type ButtonType = 'filled' | 'outlined' | 'text' | 'ghost' | 'toggle'
export type ButtonColor = 'neutral' | 'primary' | 'secondary' | 'error' | 'social' | 'reverse'
export enum BUTTON_COLOR {
	NEUTRAL = 'neutral',
	PRIMARY = 'primary',
	SECONDARY = 'secondary',
	ERROR = 'error',
	SOCIAL = 'social',
	REVERSE = 'reverse',
}

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
export enum BUTTON_SIZE {
	XS = 'xs',
	SM = 'sm',
	MD = 'md',
	LG = 'lg',
	XL = 'xl',
}

type ButtonClass =
	| typeof FilledButton
	| typeof OutlinedButton
	| typeof TextButton
	| typeof GhostButton
	| typeof ToggleButton

type ColorShade = 25 | 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900

export class ButtonStyle {
	private buttonClass: FilledButton | OutlinedButton | TextButton | GhostButton | ToggleButton

	constructor(type: ButtonType, color: ButtonColor, size: ButtonSize, onlyIcon?: boolean) {
		const buttonClass = this.getButtonClass(type)
		this.buttonClass = new buttonClass(color, size, onlyIcon)
	}

	public getClasses() {
		return this.buttonClass.getClasses()
	}

	public getStyles() {
		return this.buttonClass.getStyles()
	}

	private getButtonClass(type: ButtonType): ButtonClass {
		const defaultClass = FilledButton
		const typeMap: Record<ButtonType, ButtonClass> = {
			filled: FilledButton,
			outlined: OutlinedButton,
			text: TextButton,
			ghost: GhostButton,
			toggle: ToggleButton,
		}
		return typeMap[type] || defaultClass
	}
}

abstract class ButtonState {
	protected defaultThemeJSON = themes[0] /* 'passporter-light' */
	protected color: ButtonColor
	protected size: ButtonSize
	protected onlyIcon?: boolean

	constructor(color: ButtonColor, size: ButtonSize, onlyIcon?: boolean) {
		this.color = color
		this.size = size
		this.onlyIcon = onlyIcon
	}

	protected abstract backgroundColor(): string
	protected abstract textColor(): string
	protected abstract borderColor(): string

	protected abstract hoverBackgroundColor(): string
	protected abstract hoverBorderColor(): string

	protected abstract focusBackgroundColor(): string
	protected abstract focusBorderColor(): string

	protected abstract activeBackgroundColor(): string
	protected abstract activeBorderColor(): string

	protected abstract disabledBackgroundColor(): string
	protected abstract disabledTextColor(): string

	public getClasses(): string[] {
		return [
			this.backgroundColor(),
			this.textColor(),
			this.borderColor(),
			this.border(),
			this.padding(),
		]
	}

	public getStyles(): Record<string, string> {
		const styles: Record<string, string> = {
			'--bg-hover': this.hoverBackgroundColor(),
			'--border-hover': this.hoverBorderColor(),
			'--bg-focus': this.focusBackgroundColor(),
			'--border-focus': this.focusBorderColor(),
			'--bg-active': this.activeBackgroundColor(),
			'--border-active': this.activeBorderColor(),
			'--bg-disabled': this.disabledBackgroundColor(),
			'--text-disabled': this.disabledTextColor(),
			'--height': this.height(),
			'--width': this.width(),
		}
		const removedEmpty = this.removeEmptyStyles(styles)
		return removedEmpty
	}

	private padding() {
		const paddings: Record<ButtonSize, string> = {
			xs: 'px-1',
			sm: 'px-2',
			md: 'px-3',
			lg: this.onlyIcon ? 'px-3' : 'px-6',
			xl: this.onlyIcon ? 'px-5' : '',
		}
		return paddings[this.size]
	}

	private border(): string {
		let borderClass = ''
		if (this.borderColor()) borderClass = 'border-xs'
		return borderClass
	}

	private height() {
		const heights: Record<ButtonSize, string> = {
			xs: '24px',
			sm: '32px',
			md: '40px',
			lg: '48px',
			xl: '64px',
		}
		return heights[this.size]
	}

	private width() {
		if (!this.onlyIcon) return 'auto'
		return this.height()
	}

	private removeEmptyStyles(object: Record<string, string>): Record<string, string> {
		const newObject: Record<string, string> = {}
		for (const key in object) {
			const value = object[key]
			if (value) newObject[key] = value
		}
		return newObject
	}

	protected getColorCssVar(shade: ColorShade) {
		let color = this.color
		if (color == 'reverse') color = 'neutral'
		return `var(--color-${color}-${shade})`
	}
}

class FilledButton extends ButtonState {
	backgroundColor() {
		if (this.color == 'neutral') return `bg-neutral-800`
		if (this.color == 'reverse') return `bg-neutral-25`
		return `bg-${this.color}-500`
	}
	textColor() {
		if (this.color == 'neutral') return `text-neutral-25`
		if (this.color == 'reverse') return `text-neutral-900`
		return `text-neutral-25`
	}
	borderColor() {
		return ''
	}

	hoverBackgroundColor() {
		if (this.color == 'neutral') return this.getColorCssVar(900)
		if (this.color == 'reverse') return this.getColorCssVar(200)
		return this.getColorCssVar(600)
	}
	hoverBorderColor() {
		return ''
	}

	focusBackgroundColor() {
		if (this.color == 'neutral') return this.getColorCssVar(900)
		if (this.color == 'reverse') return this.getColorCssVar(100)
		return this.getColorCssVar(700)
	}
	focusBorderColor() {
		return ''
	}

	activeBackgroundColor() {
		if (this.color == 'neutral') return this.getColorCssVar(900)
		if (this.color == 'reverse') return this.getColorCssVar(100)
		return this.getColorCssVar(700)
	}
	activeBorderColor() {
		return ''
	}

	disabledBackgroundColor() {
		if (this.color == 'neutral') return this.getColorCssVar(200)
		if (this.color == 'reverse') return this.getColorCssVar(700)
		return this.getColorCssVar(50)
	}
	disabledTextColor() {
		if (this.color == 'reverse') return this.getColorCssVar(800)
		return this.getColorCssVar(25)
	}
}

class OutlinedButton extends ButtonState {
	backgroundColor() {
		if (this.color == 'reverse') return `bg-neutral-800`
		return `bg-neutral-25`
	}
	textColor() {
		if (this.color == 'neutral') return `text-neutral-800`
		if (this.color == 'reverse') return `text-neutral-25`
		return `text-${this.color}-500`
	}
	borderColor() {
		const specialColors: Record<string, string> = {
			reverse: 'border-neutral-200',
			neutral: 'border-neutral-200',
		}
		return specialColors[this.color] || `border-${this.color}-300`
	}

	hoverBackgroundColor() {
		if (this.color == 'reverse') return this.getColorCssVar(800)
		return this.getColorCssVar(50)
	}
	hoverBorderColor() {
		if (this.color == 'neutral') return this.getColorCssVar(800)
		if (this.color == 'reverse') return this.getColorCssVar(400)
		return this.getColorCssVar(400)
	}

	focusBackgroundColor() {
		if (this.color == 'reverse') return this.getColorCssVar(800)
		return this.getColorCssVar(25)
	}
	focusBorderColor() {
		if (this.color == 'neutral') return this.getColorCssVar(900)
		if (this.color == 'reverse') return this.getColorCssVar(300)
		return this.getColorCssVar(500)
	}

	activeBackgroundColor() {
		if (this.color == 'reverse') return this.getColorCssVar(800)
		return this.getColorCssVar(100)
	}
	activeBorderColor() {
		if (this.color == 'neutral') return this.getColorCssVar(900)
		if (this.color == 'reverse') return this.getColorCssVar(300)
		return this.getColorCssVar(500)
	}

	disabledBackgroundColor() {
		if (this.color == 'reverse') return this.getColorCssVar(800)
		return this.getColorCssVar(25)
	}
	disabledTextColor() {
		if (this.color == 'neutral') return this.getColorCssVar(300)
		if (this.color == 'reverse') return this.getColorCssVar(500)
		return this.getColorCssVar(50)
	}
}

class TextButton extends ButtonState {
	backgroundColor() {
		return 'bg-transparent'
	}
	textColor() {
		if (this.color == 'neutral') return `text-neutral-800`
		if (this.color == 'reverse') return `text-neutral-25`
		return `text-${this.color}-500`
	}
	borderColor() {
		return ''
	}

	hoverBackgroundColor() {
		if (this.color == 'reverse') return this.getColorCssVar(700)
		return this.getColorCssVar(50)
	}
	hoverBorderColor() {
		return ''
	}

	focusBackgroundColor() {
		if (this.color == 'reverse') return this.getColorCssVar(600)
		return ''
	}
	focusBorderColor() {
		return ''
	}

	activeBackgroundColor() {
		if (this.color == 'reverse') return this.getColorCssVar(600)
		return ''
	}
	activeBorderColor() {
		return ''
	}

	disabledBackgroundColor() {
		if (this.color == 'reverse') return this.getColorCssVar(800)
		return this.getColorCssVar(25)
	}
	disabledTextColor() {
		if (this.color == 'neutral') return this.getColorCssVar(300)
		if (this.color == 'reverse') return this.getColorCssVar(500)
		return this.getColorCssVar(50)
	}
}

class GhostButton extends ButtonState {
	backgroundColor() {
		return 'bg-transparent'
	}
	textColor() {
		if (this.color == 'neutral') return `text-neutral-800`
		if (this.color == 'reverse') return `text-neutral-25`
		return `text-${this.color}-500`
	}
	borderColor() {
		const specialColors: Record<string, string> = {
			reverse: 'border-neutral-200',
			neutral: 'border-neutral-800',
		}
		return specialColors[this.color] || `border-${this.color}-300`
	}

	hoverBackgroundColor() {
		if (this.color == 'reverse') return this.getColorCssVar(800)
		return this.getColorCssVar(25)
	}
	hoverBorderColor() {
		if (this.color == 'neutral') return this.getColorCssVar(800)
		if (this.color == 'reverse') return this.getColorCssVar(400)
		return this.getColorCssVar(400)
	}

	focusBackgroundColor() {
		if (this.color == 'reverse') return this.getColorCssVar(800)
		return this.getColorCssVar(25)
	}
	focusBorderColor() {
		if (this.color == 'neutral') return this.getColorCssVar(900)
		if (this.color == 'reverse') return this.getColorCssVar(300)
		return this.getColorCssVar(500)
	}

	activeBackgroundColor() {
		if (this.color == 'reverse') return this.getColorCssVar(800)
		return this.getColorCssVar(25)
	}
	activeBorderColor() {
		if (this.color == 'neutral') return this.getColorCssVar(900)
		if (this.color == 'reverse') return this.getColorCssVar(300)
		return this.getColorCssVar(500)
	}

	disabledBackgroundColor() {
		if (this.color == 'reverse') return this.getColorCssVar(800)
		return this.getColorCssVar(25)
	}
	disabledTextColor() {
		if (this.color == 'neutral') return this.getColorCssVar(300)
		if (this.color == 'reverse') return this.getColorCssVar(500)
		return this.getColorCssVar(50)
	}
}

class ToggleButton extends ButtonState {
	backgroundColor() {
		if (this.color == 'neutral') return `bg-neutral-25`
		if (this.color == 'reverse') return `bg-neutral-600`
		return `bg-${this.color}-25`
	}
	textColor() {
		if (this.color == 'neutral') return `text-neutral-800`
		if (this.color == 'reverse') return `text-neutral-25`
		return `text-neutral-800`
	}
	borderColor() {
		const specialColors: Record<string, string> = {
			reverse: 'border-neutral-800',
			neutral: 'border-neutral-200',
		}
		return specialColors[this.color] || `border-${this.color}-200`
	}

	hoverBackgroundColor() {
		if (this.color == 'neutral') return this.getColorCssVar(50)
		if (this.color == 'reverse') return this.getColorCssVar(800)
		return this.getColorCssVar(50)
	}
	hoverBorderColor() {
		if (this.color == 'neutral') return this.getColorCssVar(200)
		if (this.color == 'reverse') return this.getColorCssVar(800)
		return this.getColorCssVar(200)
	}

	focusBackgroundColor() {
		if (this.color == 'neutral') return this.getColorCssVar(50)
		if (this.color == 'reverse') return this.getColorCssVar(800)
		return this.getColorCssVar(50)
	}
	focusBorderColor() {
		if (this.color == 'neutral') return this.getColorCssVar(200)
		if (this.color == 'reverse') return this.getColorCssVar(800)
		return this.getColorCssVar(200)
	}

	activeBackgroundColor() {
		if (this.color == 'neutral') return this.getColorCssVar(50)
		if (this.color == 'reverse') return this.getColorCssVar(800)
		return this.getColorCssVar(50)
	}
	activeBorderColor() {
		if (this.color == 'neutral') return this.getColorCssVar(200)
		if (this.color == 'reverse') return this.getColorCssVar(800)
		return this.getColorCssVar(200)
	}

	disabledBackgroundColor() {
		if (this.color == 'neutral') return this.getColorCssVar(50)
		if (this.color == 'reverse') return this.getColorCssVar(700)
		return this.getColorCssVar(50)
	}
	disabledTextColor() {
		if (this.color == 'neutral') return this.getColorCssVar(300)
		if (this.color == 'reverse') return this.getColorCssVar(800)
		return this.getColorCssVar(300)
	}
}
